import { Routes, Route, Navigate } from "react-router-dom";
import { ToastProvider } from "./context/ToastContext";

/* ================= PUBLIC (WEBSITE) ================= */
import WebsiteHeader from "./components/WebsiteHeader";
import WebsiteFooter from "./components/WebsiteFooter";

/* ================= PUBLIC PAGES ================= */
import Home from "./pages/Home";
import About from "./pages/About";
import ManagementTeam from "./pages/ManagementTeam";
import Services from "./pages/Services";
import Investment from "./pages/Investment";
import Contact from "./pages/Contact";
import StaffLink from "./pages/StaffLink";
import TrackLoan from "./pages/TrackLoan";
import ApplyLoan from "./pages/ApplyLoan";

/* ================= AUTH ================= */
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import Logout from "./pages/auth/Logout";

/* ================= DASHBOARD ================= */
import DashboardLayout from "./layouts/DashboardLayout";
import ProtectedRoute from "./components/ProtectedRoute";

/* ================= ADMIN ================= */
import AdminDashboard from "./pages/dashboard/admin/AdminDashboard";
import PayslipUpload from "./pages/dashboard/admin/PayslipUpload";
import ActiveLoans from "./pages/dashboard/admin/ActiveLoans";
import RepaymentUpload from "./pages/dashboard/admin/RepaymentUpload";
import LoanSettings from "./pages/dashboard/admin/LoanSettings";
import LoanReports from "./pages/dashboard/admin/LoanReports";

/* ================= HRM / CRM ================= */
import HRMDashboard from "./pages/hrmcrm/HRMDashboard";
import CRM from "./pages/hrmcrm/CRM";
import Reports from "./pages/hrmcrm/Reports";
import StaffManagement from "./pages/hrmcrm/staff/StaffManagement";
import HRMSettings from "./pages/hrmcrm/settings/HRMSettings";

/* ================= LOANS ================= */
import LoanApplications from "./pages/dashboard/loans/LoanApplications";
import PendingLoans from "./pages/dashboard/loans/PendingLoans";
import ApprovedLoans from "./pages/dashboard/loans/ApprovedLoans";
import RejectedLoans from "./pages/dashboard/loans/RejectedLoans";
import Disbursements from "./pages/dashboard/loans/Disbursements";

import LoanCalculator from "./pages/LoanCalculator";

import CustomerLoanReply from "./pages/dashboard/loans/CustomerLoanReply";

/* ================= SETTINGS ================= */
import SystemNotifications from "./pages/settings/SystemNotifications";

/* ================= ICONS ================= */
import "bootstrap-icons/font/bootstrap-icons.css";

function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <WebsiteHeader />
      {children}
      <WebsiteFooter />
    </>
  );
}

function App() {
  return (
    <ToastProvider>
      <Routes>
        {/* ================= PUBLIC WEBSITE ================= */}
        <Route
          path="/"
          element={
            <PublicLayout>
              <Home />
            </PublicLayout>
          }
        />

        <Route
          path="/about"
          element={
            <PublicLayout>
              <About />
            </PublicLayout>
          }
        />

        <Route
          path="/management-team"
          element={
            <PublicLayout>
              <ManagementTeam />
            </PublicLayout>
          }
        />

        <Route
          path="/services"
          element={
            <PublicLayout>
              <Services />
            </PublicLayout>
          }
        />


        <Route
  path="/customer-loan-reply/:token"
  element={<CustomerLoanReply />}
/>

<Route
  path="/loan-calculator"
  element={
    <PublicLayout>
      <LoanCalculator />
    </PublicLayout>
  }
/>
        <Route
          path="/investment"
          element={
            <PublicLayout>
              <Investment />
            </PublicLayout>
          }
        />

        <Route
          path="/contact"
          element={
            <PublicLayout>
              <Contact />
            </PublicLayout>
          }
        />

        <Route
          path="/track-loan"
          element={
            <PublicLayout>
              <TrackLoan />
            </PublicLayout>
          }
        />

        <Route
          path="/track-loan/:trackingId"
          element={
            <PublicLayout>
              <TrackLoan />
            </PublicLayout>
          }
        />

        <Route path="/applyloan/:staffId" element={<StaffLink />} />
        <Route path="/applyloan" element={<ApplyLoan />} />

        {/* ================= AUTH ================= */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/logout" element={<Logout />} />

        {/* ================= DASHBOARD ================= */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<Navigate to="hrmcrm" replace />} />

            {/* HRM / CRM */}
            <Route path="hrmcrm" element={<HRMDashboard />} />
            <Route path="hrmcrm/crm" element={<CRM />} />
            <Route path="hrmcrm/staff" element={<StaffManagement />} />
            <Route path="hrmcrm/reports" element={<Reports />} />
            <Route path="hrmcrm/settings" element={<HRMSettings />} />

            {/* LOANS */}
            <Route path="loans/applications" element={<LoanApplications />} />
            <Route path="loans/pending" element={<PendingLoans />} />
            <Route path="loans/approved" element={<ApprovedLoans />} />
            <Route path="loans/rejected" element={<RejectedLoans />} />
            <Route path="loans/disbursements" element={<Disbursements />} />

            {/* SETTINGS */}
            <Route
              path="settings/notifications"
              element={<SystemNotifications />}
            />

            {/* ADMIN ONLY */}
            <Route element={<ProtectedRoute adminOnly />}>
              <Route path="admin" element={<AdminDashboard />} />
              <Route
                path="admin/loan-management"
                element={<LoanApplications />}
              />
              <Route path="admin/payslip-upload" element={<PayslipUpload />} />
              <Route path="admin/active-loans" element={<ActiveLoans />} />
              <Route path="admin/loan-reports" element={<LoanReports />} />
              <Route
                path="admin/repayment-upload"
                element={<RepaymentUpload />}
              />
              <Route path="admin/loan-settings" element={<LoanSettings />} />
            </Route>
          </Route>
        </Route>

        {/* ================= FALLBACK ================= */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ToastProvider>
  );
}

export default App;